import { useState, useCallback, useRef } from "react";
import { Account, useAppDispatch, useDownloadFileWeb, DownloadRequest, DownloadProgressTracker } from "src/common";
import { driveActions } from "src/store";


/* Description:  Hook for bulk downloading files */
export function useBulkDownloadFiles(current_account: Account, use_filesync: boolean = false) {
  const [progress, setProgress] = useState<DownloadProgressTracker[]>([]);
  const { downloadFile } = useDownloadFileWeb(current_account, use_filesync, handleFileComplete, handleSetProgress);
  const dispatch = useAppDispatch();
  const node_queue_total_ref = useRef<number>(0); // Total number of requests
  const node_queue_ref = useRef<DownloadRequest[]>([]);
  const working_node_ref = useRef<DownloadRequest>();

  // ----------------------------------------------------------------------------------------------------
  // Description: Handle Queue and progress and updates, build data
  // ----------------------------------------------------------------------------------------------------
  // Description: Move to next item in queue
  function getNext() {
    working_node_ref.current = node_queue_ref.current.shift();
    if (!!working_node_ref.current) {
      handleSetProgress(working_node_ref.current);
      downloadFile(working_node_ref.current);
    } else {
      handleSuccess();
    }
  }

  /* Description:  Updates the progress every time its updated to display in the UI */
  function handleSetProgress(request: DownloadRequest, done: number = 0, total: number = 1, error?: string) {
    // NOTE: new request start at done = 0 and total = 1
    const file_id = request.file_id;
    const _progress = {
      file_id,
      file_name: request.file_name,
      done,
      total,
      error
    };
    setProgress((progress) => {
      const index = progress.findIndex(t => t.file_id === file_id);
      return index === -1 ? progress.concat(_progress) : progress.map((t, i) => i === index ? _progress : t);
    });
  }

  // Description: Complete the file download
  function handleFileComplete(_file_request?: DownloadRequest, error_message?: string) {
    const download_complete = Object.assign({}, _file_request || working_node_ref.current);
    (!!error_message && !!download_complete) && handleSetProgress(download_complete, 0, 1, error_message);
    getNext();
  }

  // Description: Description: When all files have been downloaded/errored out, we reset the progress, the download request and the download queue after 5 seconds.
  function handleSuccess() {
    // NOTE: timeout to allow user to see the progress before the view closes.
    setTimeout(() => {
      reset();
      dispatch(driveActions.setFileDownloadRequest(undefined));
    }, 5000);
  }

  // ----------------------------------------------------------------------------------------------------
  // Description: Callbacks and general calls
  // ----------------------------------------------------------------------------------------------------
  // Description: 
  function reset() {
    node_queue_total_ref.current = 0;
    node_queue_ref.current = [];
    working_node_ref.current = undefined;
    setProgress([]);
  }

  // Description: Callback for download files - adds files to download queue and starts downloading one file at a time
  const downloadFiles = useCallback((requests: DownloadRequest[]) => {
    node_queue_ref.current = requests.slice();
    requests.forEach(request => (handleSetProgress(request)));
    getNext();
  }, [progress]);

  // Description: Reset Downloads
  const resetBulkDownload = useCallback(() => {
    reset();
  }, []);

  return {
    downloadFiles,
    resetBulkDownload,
    progress
  };
}
